import { CSSProperties, memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useInViewport } from 'hooks/useInViewport';

import AvatarFrame from 'components/Streaks/AvatarFrame';
import Flame from 'components/Streaks/Flame';
import Freeze from 'components/Streaks/Freeze';
import { BannerStubItem, deriveBannerView, StreakView } from 'components/Streaks/model/deriveView';

import { avatarStack, BannerHeight, STUB_VISUAL } from './stubGeometry';

import styles from './StreakBanner.module.scss';

const TINTS = [
  'rgba(0,0,0,0)',
  'rgba(232,90,30,0.30)',
  'rgba(240,140,40,0.32)',
  'rgba(255,190,60,0.34)',
  'rgba(255,225,140,0.42)',
  'rgba(160,205,255,0.40)',
  'rgba(90,150,255,0.36)'
];

/** Stub items take 4 s turns (EN-11155). */
export const STUB_TURN_MS = 4000;

export interface StreakBannerProps {
  view: StreakView;
  height: BannerHeight;
  /** The signed-in player's own avatar, worn by the stub's frame preview. */
  avatarSrc?: string;
  /** Art for a status's avatar set; empty while the pool lacks it (EN-11264). */
  statusAvatars?: (setKey: string | undefined) => string[];
  reducedMotion: boolean;
  onOpen: () => void;
}

/** True while the document is visible; the rotation and scroll stop otherwise. */
function usePageVisible(): boolean {
  const [visible, setVisible] = useState(
    () => typeof document === 'undefined' || document.visibilityState !== 'hidden'
  );
  useEffect(() => {
    const onChange = () => setVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  return visible;
}

/**
 * Index of the stub item on show. One timer, only while there is more than
 * one item and the banner is actually visible; reset to the first item when
 * the items change so every state starts on its first item.
 */
function useRotation(count: number, running: boolean, resetKey: string): number {
  const [index, setIndex] = useState(0);
  useEffect(() => setIndex(0), [resetKey]);
  useEffect(() => {
    if (!running || count < 2) return undefined;
    const id = window.setInterval(() => setIndex((i) => (i + 1) % count), STUB_TURN_MS);
    return () => window.clearInterval(id);
  }, [count, running]);
  return count < 2 ? 0 : index;
}

/**
 * A line that fits is plain text; one that does not scrolls (EN-11155
 * "Nothing truncates"). Measured from the rendered text, not guessed.
 */
function FitLine({ text, running }: { text: string; running: boolean }) {
  const clipRef = useRef<HTMLSpanElement>(null);
  const firstRef = useRef<HTMLSpanElement>(null);
  const [overflow, setOverflow] = useState<number | null>(null);

  useLayoutEffect(() => {
    const clip = clipRef.current;
    const first = firstRef.current;
    if (!clip || !first) return undefined;
    const measure = () => {
      const pad = parseFloat(getComputedStyle(first).paddingRight) || 0;
      const textW = first.scrollWidth - pad;
      setOverflow(textW > clip.clientWidth + 0.5 ? textW : null);
    };
    measure();
    // The web font can land after first paint and widen the text without the
    // column changing size, so watch the text itself and the font load too.
    let alive = true;
    document.fonts?.ready.then(() => alive && measure());
    if (typeof ResizeObserver === 'undefined') {
      return () => {
        alive = false;
      };
    }
    const ro = new ResizeObserver(measure);
    ro.observe(clip);
    ro.observe(first);
    return () => {
      alive = false;
      ro.disconnect();
    };
  }, [text]);

  // ~30 px/s over the text plus its 32 px gap, with a 30% hold at the start.
  const seconds = overflow ? Math.max(8, Math.round((overflow + 32) / 30 / 0.7)) : 0;
  return (
    <span
      ref={clipRef}
      className={`${styles.lineClip} ${overflow ? styles.scrolling : ''} ${
        running ? '' : styles.paused
      }`}
      style={overflow ? ({ '--scroll-s': `${seconds}s` } as CSSProperties) : undefined}
      aria-label={overflow ? text : undefined}>
      <span className={styles.lineTrack} aria-hidden={overflow ? true : undefined}>
        <span ref={firstRef}>{text}</span>
        {overflow && <span>{text}</span>}
      </span>
    </span>
  );
}

function StubVisual({
  item,
  height,
  avatarSrc,
  statusAvatars
}: {
  item: BannerStubItem;
  height: BannerHeight;
  avatarSrc?: string;
  statusAvatars?: (setKey: string | undefined) => string[];
}) {
  const sizes = STUB_VISUAL[height];
  if (item.kind === 'text') {
    const size = item.value.length >= 6 ? 18 : item.value.length >= 5 ? 20 : 23;
    return (
      <span
        className={styles.stubValue}
        style={{ fontSize: size, color: item.tone === 'alert' ? '#8a2c0b' : '#1a0e06' }}>
        {item.value}
      </span>
    );
  }
  if (item.kind === 'frame') {
    return <AvatarFrame tier={item.tier} size={sizes.avatar} src={avatarSrc} />;
  }
  if (item.kind === 'freeze') return <Freeze size={sizes.freeze} />;
  const m = item.milestone;
  const srcs = statusAvatars?.(m.avatarSetKey) ?? [];
  const stack = avatarStack(item.slots, height);
  return (
    <span className={styles.stack} style={{ width: stack.width, height: stack.height }}>
      {stack.dots.map((dot, i) => (
        <span
          key={i}
          className={styles.stackDot}
          style={{ left: dot.x, top: dot.y, width: dot.d, height: dot.d }}>
          {srcs[i] && (
            <img
              src={srcs[i]}
              alt=''
              style={{ left: -dot.crop, top: -dot.crop, width: dot.big, height: dot.big }}
            />
          )}
        </span>
      ))}
    </span>
  );
}

function StreakBanner({
  view,
  height,
  avatarSrc,
  statusAvatars,
  reducedMotion,
  onOpen
}: StreakBannerProps) {
  const banner = deriveBannerView(view);
  const ref = useRef<HTMLButtonElement>(null);
  const inView = useInViewport(ref);
  const pageVisible = usePageVisible();
  const running = inView && pageVisible && !reducedMotion;
  const stubKey = `${view.state}:${view.days}:${banner.stub.length}`;
  const index = useRotation(banner.stub.length, running, stubKey);
  const isText = banner.stub[index]?.kind === 'text';
  const stage = view.flameStage;
  const tint = stage === 'ember' ? 'rgba(184,95,18,0.22)' : TINTS[stage];
  const flare = banner.flare && !reducedMotion;
  const sizeClass = height === 64 ? styles.header : height === 80 ? styles.tall : '';

  return (
    <button
      ref={ref}
      type='button'
      className={`${styles.banner} ${sizeClass} ${reducedMotion ? styles.still : ''}`}
      onClick={onOpen}
      aria-label={`Your streak: ${view.days} ${view.days === 1 ? 'day' : 'days'}. ${
        banner.headline
      }. Open streak details`}
      data-testid='streak-banner'
      data-state={view.state}>
      <svg className={styles.grain} aria-hidden='true'>
        <filter id='sbGrain'>
          <feTurbulence
            type='fractalNoise'
            baseFrequency='0.85'
            numOctaves={2}
            stitchTiles='stitch'
          />
          <feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.18 0' />
        </filter>
        <rect width='100%' height='100%' filter='url(#sbGrain)' />
      </svg>
      <span
        className={styles.tint}
        aria-hidden='true'
        style={{ background: `radial-gradient(closest-side, ${tint}, rgba(0,0,0,0))` }}
      />
      <span className={styles.dashed} aria-hidden='true' />

      <span className={styles.row}>
        <span className={styles.flame}>
          <Flame
            key={flare ? stubKey : 'still'}
            stage={stage}
            size={height === 64 ? 42 : 48}
            low={view.state === 'lastcall'}
            flare={flare}
          />
        </span>

        <span className={styles.count} aria-hidden='true'>
          <span
            className={styles.countValue}
            style={{ color: view.days === 0 ? '#8a6a4a' : '#1a0e06' }}>
            {view.days}
          </span>
          <span className={styles.countLabel}>{view.days === 1 ? 'Day' : 'Days'}</span>
        </span>

        <span className={styles.text}>
          <span className={`${styles.headline} ${banner.clock ? styles.alert : ''}`}>
            {banner.clock && (
              <svg
                className={styles.clock}
                width='13'
                height='13'
                viewBox='0 0 24 24'
                fill='none'
                stroke='#8a2c0b'
                strokeWidth='2.4'
                strokeLinecap='round'
                aria-hidden='true'>
                <circle cx='12' cy='12' r='9' />
                <path d='M12 7v5l3 2' />
              </svg>
            )}
            <FitLine text={banner.headline} running={running} />
          </span>

          {banner.second.kind === 'meter' ? (
            <span className={styles.meter}>
              <span className={styles.meterLabel} aria-hidden='true'>
                Flops
              </span>
              {view.flopsNeeded <= 5 ? (
                <span className={styles.pips} aria-hidden='true'>
                  {Array.from({ length: view.flopsNeeded }, (_, i) => (
                    <span
                      key={i}
                      className={`${styles.pip} ${i < view.flops ? styles.pipOn : ''}`}
                    />
                  ))}
                </span>
              ) : (
                <>
                  <span className={styles.bar} aria-hidden='true'>
                    <span
                      style={{ width: `${Math.round((view.flops / view.flopsNeeded) * 100)}%` }}
                    />
                  </span>
                  <span className={styles.barValue} aria-hidden='true'>
                    {view.flops}/{view.flopsNeeded}
                  </span>
                </>
              )}
              <span className={styles.srOnly}>{banner.meterText}</span>
            </span>
          ) : (
            <span className={styles.checkLine}>
              <svg
                width='12'
                height='12'
                viewBox='0 0 24 24'
                fill='none'
                stroke='#b85f12'
                strokeWidth='3.5'
                strokeLinecap='round'
                strokeLinejoin='round'
                aria-hidden='true'
                style={{ flexShrink: 0 }}>
                <path d='M5 12l5 5L20 7' />
              </svg>
              <FitLine text={banner.second.text} running={running} />
            </span>
          )}
        </span>

        <span
          className={`${styles.stub} ${isText ? styles.stubText : ''} ${
            view.state === 'milestone' ? styles.celebrate : ''
          }`}
          aria-hidden='true'>
          <span className={styles.divider} />
          {banner.sharedLabel && (
            <span className={styles.stubItem + ' ' + styles.on}>
              <span className={styles.stubLabel}>{banner.sharedLabel}</span>
            </span>
          )}
          {banner.stub.map((item, i) => (
            <span
              key={`${stubKey}:${i}`}
              className={`${styles.stubItem} ${
                i === index
                  ? styles.on
                  : i === (index + banner.stub.length - 1) % banner.stub.length &&
                    banner.stub.length > 1
                  ? styles.off
                  : ''
              }`}>
              <span className={`${styles.stubLabel} ${banner.sharedLabel ? styles.hidden : ''}`}>
                {item.label}
              </span>
              <span className={styles.stubSlot}>
                <StubVisual
                  item={item}
                  height={height}
                  avatarSrc={avatarSrc}
                  statusAvatars={statusAvatars}
                />
              </span>
              <span className={styles.stubSub}>{item.sub}</span>
            </span>
          ))}
        </span>
      </span>

      {flare && <span key={`sheen:${stubKey}`} className={styles.sheen} aria-hidden='true' />}
    </button>
  );
}

export default memo(StreakBanner);
